// Cookie consent banner. Mounted on every route at the landing app root,
// renders only where the marketing tracker can fire and when no prior choice
// exists. Persists the visitor's choice via the ``consent`` module so a
// returning visitor is not re-prompted.

import { useLocation } from "react-router-dom";

import { useConsent } from "./consent";
import { isPublicPath } from "./publicPaths";


export function ConsentBanner(): JSX.Element | null {
  const { pathname } = useLocation();
  const { status, accept, reject } = useConsent();

  if (!isPublicPath(pathname)) return null;
  if (status !== null) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className={[
        "fixed inset-x-0 bottom-0 z-50",
        "border-t border-border-subtle bg-raised shadow-overlay",
      ].join(" ")}
    >
      <div
        className={[
          "mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 text-sm",
          "sm:flex-row sm:items-center sm:justify-between",
        ].join(" ")}
      >
        <p className="text-text-secondary">
          We use cookies to understand how visitors find BeneCRM. Marketing
          cookies stay off until you opt in — your choice is remembered on
          this device.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            data-testid="cookie-consent-reject"
            onClick={reject}
            className={[
              "rounded-md border border-border-subtle px-3 py-1.5",
              "text-sm text-text-primary hover:bg-recessed",
            ].join(" ")}
          >
            Reject
          </button>
          <button
            type="button"
            data-testid="cookie-consent-accept"
            onClick={accept}
            className={[
              "rounded-md bg-teal px-3 py-1.5 text-sm font-medium",
              "text-text-inverse hover:opacity-90",
            ].join(" ")}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
